/**
 * Status panel: rate budget, tick health, what is blocking the loop.
 *
 * The rate gauge is the important part — Torn's limit is per account, so the
 * meter is drawn against `accounts × 100` rather than a flat 100, and the
 * caption says which it is.
 */

import {
  budgetPerMin,
  CALLS_PER_MIN_LIMIT,
  distinctAccounts,
  globalPauseRemainingMs,
  totalCallsInWindow,
} from "../api/keyring.ts";
import { engine$ } from "../engine/scheduler.ts";
import { keys$, settings$, watchlist$ } from "../state/store.ts";
import { card, clear, el, append } from "./dom.ts";
import { relativeTime } from "../util/format.ts";
import { TORN_CACHE_DELAY_SEC } from "../api/torn.ts";

export function createStatusPanel(): HTMLElement {
  const callsText = el("span", { class: "mono fw-semibold" });
  const meterBar = el("div", { class: "progress-bar" });
  const meter = el(
    "div",
    { class: "progress rate-meter", role: "progressbar", "aria-label": "API calls per minute" },
    meterBar,
  );

  const lastTick = el("span", { class: "mono" });
  const cadence = el("span", { class: "mono" });
  const accountsBadge = el("span", { class: "badge text-bg-light" });
  const alerts = el("div", { class: "d-flex flex-column gap-2 mt-3" });

  const body = el(
    "div",
    {},
    el(
      "div",
      { class: "d-flex justify-content-between align-items-center mb-1" },
      el("span", { class: "control-label", text: "API calls" }),
      callsText,
    ),
    meter,
    el(
      "div",
      { class: "row g-2 mt-2" },
      el(
        "div",
        { class: "col-6" },
        el("div", { class: "control-label", text: "Last tick" }),
        lastTick,
      ),
      el(
        "div",
        { class: "col-6" },
        el("div", { class: "control-label", text: "Projected load" }),
        cadence,
      ),
    ),
    alerts,
  );

  /**
   * What the current watchlist and interval will cost per minute. Bazaar polls
   * go to weav3r and are free, so only the Torn side counts.
   */
  function projectedCallsPerMin(): number {
    const settings = settings$.get();
    const interval = Math.max(TORN_CACHE_DELAY_SEC, settings.intervalSec);
    return Math.ceil((watchlist$.get().length * 60) / interval);
  }

  function render(): void {
    const entries = keys$.get();
    const accounts = distinctAccounts(entries);
    const budget = budgetPerMin(entries);
    const used = totalCallsInWindow();

    callsText.textContent = `${used}/${budget || CALLS_PER_MIN_LIMIT} / min`;

    const ratio = budget > 0 ? Math.min(100, (used / budget) * 100) : 0;
    meterBar.style.width = `${ratio}%`;
    meterBar.className = `progress-bar ${ratio > 90 ? "bg-danger" : ratio > 70 ? "bg-warning" : "bg-success"}`;
    meter.setAttribute("aria-valuenow", String(Math.round(ratio)));

    accountsBadge.textContent =
      entries.length === 0
        ? "no keys"
        : `${entries.length} ${entries.length === 1 ? "key" : "keys"} · ${accounts} ${accounts === 1 ? "account" : "accounts"}`;
    accountsBadge.title =
      accounts >= 2
        ? "Multiple accounts — Torn's 100/min cap is per account, so throughput scales."
        : "All keys belong to one account, so they share a single 100/min budget.";

    const engine = engine$.get();
    lastTick.textContent = relativeTime(engine.lastTickAt);

    const projected = projectedCallsPerMin();
    cadence.textContent = `${projected}/min`;

    clear(alerts);

    if (entries.every((entry) => entry.health !== "valid")) {
      append(
        alerts,
        el("div", {
          class: "alert alert-danger tiny py-2 px-3 mb-0",
          text: "Add a valid API key to start polling.",
        }),
      );
    } else if (budget > 0 && projected > budget) {
      append(
        alerts,
        el("div", {
          class: "alert alert-warning tiny py-2 px-3 mb-0",
          text:
            `Watchlist needs ~${projected} calls/min but the budget is ${budget}. ` +
            "Raise the interval, trim the list, or add a key from another account.",
        }),
      );
    }

    const pausedMs = globalPauseRemainingMs();
    if (pausedMs > 0) {
      append(
        alerts,
        el("div", {
          class: "alert alert-danger tiny py-2 px-3 mb-0",
          text: `Torn IP block — paused for ${Math.ceil(pausedMs / 1000)}s.`,
        }),
      );
    }

    if (engine.blockedReason) {
      append(
        alerts,
        el("div", { class: "alert alert-warning tiny py-2 px-3 mb-0", text: engine.blockedReason }),
      );
    }

    if (engine.lastError) {
      append(
        alerts,
        el("div", { class: "alert alert-danger tiny py-2 px-3 mb-0", text: engine.lastError }),
      );
    }
  }

  engine$.subscribe(render, false);
  keys$.subscribe(render, false);
  watchlist$.subscribe(render, false);
  settings$.subscribe(render, false);

  // The rolling window and "x ago" labels drift on their own, so repaint on a timer.
  window.setInterval(render, 1000);
  render();

  return card("Status", body, accountsBadge);
}
